/**
 * Time range picker — a compact trigger showing the APPLIED range, and a popover to change it.
 *
 *   ┌ Time range ─────────────── 9h ┐
 *   │ [Whole day] [Footage] [Last 1h] │   presets apply at once and close
 *   │ [00–06] [06–12] [12–18] [18–24] │
 *   │ From  [ 3 ▾]:[00 ▾] [AM|PM]     │   hour / minute are native selects: keyboard- and
 *   │ To    [12 ▾]:[00 ▾] [AM|PM]     │   wheel-friendly, and nothing to scroll or snap back
 *   │                 Cancel  Apply   │
 *
 * Enter applies, Escape / Cancel / a click outside reverts to the applied range.
 *
 * The panel is portaled to <body> and placed in viewport coordinates, flipping to whichever side
 * of the trigger has room — inside a short, overflow-hidden column (the playback bars) an
 * in-flow popover would be clipped exactly when someone needs it.
 *
 * Values are "HH:mm", 24-hour, local time. The trigger shows 24-hour times ("03:00–12:00") —
 * the shortest unambiguous form, which is what a one-row control bar has room for.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClockIcon } from '@heroicons/react/24/outline';
import { Tip } from '../common/Tooltip';
import { BTN_PRIMARY_SM, BTN_SECONDARY_SM } from '../common/ui';

export interface TimeRange { start: string; end: string }

export interface TimeRangePickerProps {
  value: TimeRange;
  onApply: (next: TimeRange) => void;
  /** Where the day's footage starts / ends ("HH:mm") — enables the Footage and Last 1h presets. */
  footage?: TimeRange | null;
  className?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const parseHM = (v: string) => {
  const [H, M] = (v || '00:00').split(':').map(Number);
  return { h12: (H % 12) || 12, min: M || 0, pm: H >= 12 };
};
const buildHM = (h12: number, min: number, pm: boolean) => `${pad((h12 % 12) + (pm ? 12 : 0))}:${pad(min)}`;
const toMin = (hm: string) => { const [h, m] = hm.split(':').map(Number); return h * 60 + (m || 0); };
const fromMin = (m: number) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
export const fmt12 = (v: string) => {
  const { h12, min, pm } = parseHM(v);
  return `${h12}:${pad(min)} ${pm ? 'PM' : 'AM'}`;
};
function fmtLen(start: string, end: string) {
  const m = toMin(end) - toMin(start);
  if (m <= 0) return '';
  const h = Math.floor(m / 60);
  return h ? (m % 60 ? `${h}h ${m % 60}m` : `${h}h`) : `${m}m`;
}

const PANEL_W = 256;
const EDGE = 8;

export default function TimeRangePicker({ value, onApply, footage, className = '' }: TimeRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<TimeRange>(value);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The applied range can change from outside (a new day fits the range to its footage).
  // By field, not by object: hosts pass a fresh `{ start, end }` every render.
  useEffect(() => {
    if (!open) setPending((p) => (p.start === value.start && p.end === value.end ? p : { start: value.start, end: value.end }));
  }, [value.start, value.end, open]);

  const valid = pending.start < pending.end;

  const close = useCallback((apply: boolean) => {
    if (apply && pending.start < pending.end) onApply(pending);
    else setPending(value);
    setOpen(false);
  }, [pending, value, onApply]);

  const applyNow = (next: TimeRange) => {
    if (!(next.start < next.end)) return;
    setPending(next);
    onApply(next);
    setOpen(false);
  };

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const H = panelRef.current?.offsetHeight || 230;
    const above = r.top - H - 6;
    const top = Math.max(EDGE, above >= EDGE ? above : Math.min(r.bottom + 6, window.innerHeight - H - EDGE));
    const left = Math.max(EDGE, Math.min(r.left, window.innerWidth - PANEL_W - EDGE));
    // Same place → same object, so nothing downstream re-runs.
    setPos((p) => (p && p.top === top && p.left === left ? p : { top, left }));
  }, []);

  // Re-measure while open: a scroll or resize moves the trigger, and a fixed panel does not follow.
  // Scrolls inside the panel itself are not a reason to move.
  useEffect(() => {
    if (!open) { setPos(null); return undefined; }
    place();
    const onScroll = (e: Event) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      place();
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, place]);

  // Click outside = Cancel. The panel is outside the trigger's DOM subtree, so it needs its own check.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
      else if (e.key === 'Enter') { e.preventDefault(); close(true); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const setPart = (which: 'start' | 'end', part: 'h12' | 'min' | 'pm', val: number | boolean) => {
    setPending((p) => {
      const cur = parseHM(p[which]);
      const next = { ...cur, [part]: val };
      return { ...p, [which]: buildHM(next.h12, next.min, next.pm) };
    });
  };

  const presets: { label: string; range: TimeRange | null; title: string }[] = [
    { label: 'Whole day', range: { start: '00:00', end: '23:59' }, title: '00:00 – 23:59' },
    {
      label: 'Footage',
      range: footage && footage.start < footage.end ? footage : null,
      title: footage ? `Where this day's recordings are: ${footage.start} – ${footage.end}` : 'No footage on this day',
    },
    {
      label: 'Last 1h',
      range: footage && footage.start < footage.end
        ? { start: fromMin(Math.max(toMin(footage.start), toMin(footage.end) - 60)), end: footage.end }
        : null,
      title: footage ? `The last hour of this day's footage` : 'No footage on this day',
    },
    { label: '00–06', range: { start: '00:00', end: '06:00' }, title: 'Night: 12:00 AM – 6:00 AM' },
    { label: '06–12', range: { start: '06:00', end: '12:00' }, title: 'Morning: 6:00 AM – 12:00 PM' },
    { label: '12–18', range: { start: '12:00', end: '18:00' }, title: 'Afternoon: 12:00 PM – 6:00 PM' },
    { label: '18–24', range: { start: '18:00', end: '23:59' }, title: 'Evening: 6:00 PM – 11:59 PM' },
  ];

  const row = (which: 'start' | 'end', label: string) => {
    const v = parseHM(pending[which]);
    const sel = 'h-7 rounded-lg border border-border bg-background px-1 font-mono text-[12px] text-foreground focus:border-brand focus:outline-none';
    const ampm = (pm: boolean) =>
      `px-2 text-[11px] font-semibold transition-colors ${v.pm === pm ? 'bg-brand text-white' : 'text-muted-foreground hover:bg-muted'}`;
    return (
      <div className="flex items-center gap-1.5">
        <span className="w-9 text-[11px] font-medium text-muted-foreground">{label}</span>
        <select aria-label={`${label} hour`} value={v.h12} onChange={(e) => setPart(which, 'h12', Number(e.target.value))} className={sel}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
        <span className="text-muted-foreground">:</span>
        <select aria-label={`${label} minute`} value={v.min} onChange={(e) => setPart(which, 'min', Number(e.target.value))} className={sel}>
          {Array.from({ length: 60 }, (_, i) => i).map((m) => <option key={m} value={m}>{pad(m)}</option>)}
        </select>
        <div className="ml-auto inline-flex h-7 overflow-hidden rounded-lg border border-border divide-x divide-border" role="group" aria-label={`${label} AM or PM`}>
          <button type="button" onClick={() => setPart(which, 'pm', false)} className={ampm(false)} aria-pressed={!v.pm}>AM</button>
          <button type="button" onClick={() => setPart(which, 'pm', true)} className={ampm(true)} aria-pressed={v.pm}>PM</button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Tip label={`Time range: ${fmt12(value.start)} – ${fmt12(value.end)} (${fmtLen(value.start, value.end)}) — click to change`} disabled={open}>
      <button
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Time range"
        className={`flex h-7 shrink-0 items-center gap-1 rounded-lg border px-1.5 text-[11px] text-foreground transition-colors hover:bg-muted ${
          open ? 'border-brand ring-1 ring-brand-ring' : 'border-border'
        } ${className}`}
      >
        <ClockIcon className="h-3.5 w-3.5 flex-none text-muted-foreground" />
        <span className="font-mono tabular-nums">{value.start}–{value.end}</span>
      </button>
      </Tip>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Pick a time range"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: PANEL_W, zIndex: 9999 }}
          className="space-y-2.5 rounded-lg border border-border bg-card p-3 shadow-modal"
        >
          <div className="flex items-baseline justify-between">
            <span className="text-[12px] font-semibold text-foreground">Time range</span>
            <span className={`font-mono text-[11px] ${valid ? 'text-muted-foreground' : 'text-red-500'}`}>
              {valid ? fmtLen(pending.start, pending.end) : 'end before start'}
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {presets.map((p) => {
              const on = !!p.range && p.range.start === value.start && p.range.end === value.end;
              return (
                <Tip key={p.label} label={p.title}>
                <button
                  type="button"
                  disabled={!p.range}
                  onClick={() => p.range && applyNow(p.range)}
                  className={`h-6 rounded-lg border px-1.5 text-[11px] transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    on ? 'border-brand bg-brand text-white' : 'border-border text-foreground hover:bg-muted'
                  } ${/^\d/.test(p.label) ? 'font-mono' : 'font-medium'}`}
                >
                  {p.label}
                </button>
                </Tip>
              );
            })}
          </div>
          <div className="space-y-1.5 border-t border-border pt-2.5">
            {row('start', 'From')}
            {row('end', 'To')}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => close(false)}
              className={BTN_SECONDARY_SM}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => close(true)}
              disabled={!valid}
              className={BTN_PRIMARY_SM}
            >
              Apply
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
